<script setup>
import { computed, reactive } from 'vue'
import { t, countLabel, schemaText } from '../lib/i18n.mjs'
import { displayedMatches } from '../lib/motifs.mjs'
import { miniature } from '../lib/motif-gallery.mjs'
import MotifPreview from './MotifPreview.vue'
import FacetHatchPattern from './FacetHatchPattern.vue'
import { nodeLabels } from '../lib/node-display.mjs'
const props = defineProps({
  document: Object,
  matchingDocument: Object,
  motifs: Array,
  schemas: Array,
  links: Array,
  filterIds: Object,
  stripeIds: Object,
})
defineEmits(['choose'])
const shown = reactive({})
const nodeTypes = computed(() => Object.fromEntries(props.schemas.map((s) => [s.id, s])))
const linkTypes = computed(() => Object.fromEntries(props.links.map((s) => [s.id, s])))
const labels = computed(() => nodeLabels(props.document, props.schemas))
const groups = computed(() =>
  props.motifs.map((motif) => {
    const matches = displayedMatches(
      props.matchingDocument || props.document,
      motif,
      250,
      props.schemas,
      props.links,
      props.document,
    )
    return {
      motif,
      total: matches.length,
      matches: matches.filter(
        (match) => !props.filterIds || match.nodeIds.some((id) => props.filterIds.has(id)),
      ),
    }
  }),
)
const cards = (group) =>
  group.matches
    .slice(0, shown[group.motif.id] || 12)
    .map((match) => ({ match, ...miniature(props.document, match, props.schemas, labels.value) }))
</script>
<template>
  <div class="motif-gallery" :aria-label="t('Motif 图集')">
    <p class="gallery-intro">{{ t('按 Motif 查看当前文档中的匹配子图，点击小图返回主图定位。') }}</p>
    <section v-for="group in groups" :key="group.motif.id" class="gallery-group">
      <header>
        <MotifPreview :motif="group.motif" />
        <h3>{{ schemaText(group.motif, 'name', 'motif') }}</h3>
        <span>{{ countLabel(group.matches.length >= 250 ? '250+' : group.matches.length, '个匹配') }}</span>
      </header>
      <div v-if="group.matches.length" class="gallery-grid">
        <button
          v-for="(card, index) in cards(group)"
          :key="card.match.nodeIds.join(':') + card.match.edgeIds.join(':')"
          class="motif-card"
          :data-node-ids="card.match.nodeIds.join(' ')"
          :aria-label="t('定位匹配 {n}', { n: index + 1 })"
          @click="$emit('choose', card.match)"
        >
          <svg :viewBox="`0 0 ${card.width} ${card.height}`" aria-hidden="true">
            <defs><FacetHatchPattern :id="`gallery-hatch-${group.motif.id}-${index}`" /></defs>
            <g
              v-for="edge in card.edges"
              :key="edge.id"
              :stroke="linkTypes[edge.type]?.color || '#9aa5af'"
              fill="none"
            >
              <path
                :d="edge.path"
                stroke-width="1.3"
                :stroke-dasharray="
                  linkTypes[edge.type]?.dash === 'dashed'
                    ? '7 5'
                    : linkTypes[edge.type]?.dash === 'dotted'
                      ? '2 4'
                      : undefined
                "
              />
              <polyline :points="edge.arrow" />
              <title>
                {{ schemaText(linkTypes[edge.type], 'name', 'link') }}{{ edge.text ? ` · ${edge.text}` : '' }}
              </title>
            </g>
            <g
              v-for="node in card.nodes"
              :key="node.id"
              class="gallery-node"
              :class="{ 'is-faceted': stripeIds?.has(node.id) }"
              :data-node-id="node.id"
              :data-level="node.paragraphId ? 'unit' : 'paragraph'"
              :transform="`translate(${node.x},${node.y})`"
            >
              <rect
                :x="-node.width / 2"
                :y="-node.height / 2"
                :width="node.width"
                :height="node.height"
                rx="2"
                fill="#ffffff"
                :stroke="nodeTypes[node.type]?.color || '#bcc2c8'"
                stroke-width="1.5"
              />
              <rect
                v-if="stripeIds?.has(node.id)"
                class="facet-hatch"
                :x="-node.width / 2"
                :y="-node.height / 2"
                :width="node.width"
                :height="node.height"
                rx="2"
                :fill="`url(#gallery-hatch-${group.motif.id}-${index})`"
                :opacity="node.paragraphId ? 0.2 : 0.1"
                pointer-events="none"
              />
              <rect
                :x="-node.width / 2"
                :y="-node.height / 2"
                width="3"
                :height="node.height"
                :fill="nodeTypes[node.type]?.color || '#bcc2c8'"
              />
              <text y="4" text-anchor="middle" :font-size="node.paragraphId ? 11 : 14">{{ node.label }}</text>
              <title>{{ node.detail || t('尚无段落摘要') }}</title>
            </g>
          </svg>
          <span class="motif-card-ids">{{ card.match.nodeIds.join(' · ') }}</span>
        </button>
      </div>
      <p v-if="group.total >= 250" class="gallery-empty">
        {{ t('每类最多检索 250 个匹配，可分批显示。') }}
      </p>
      <p v-if="!group.matches.length" class="gallery-empty">{{ t('暂无匹配子图') }}</p>
      <button
        v-if="group.matches.length > (shown[group.motif.id] || 12)"
        class="subtle-button gallery-more"
        @click="shown[group.motif.id] = (shown[group.motif.id] || 12) + 12"
      >
        {{ t('显示更多') }}
      </button>
    </section>
    <p v-if="!groups.length" class="gallery-empty">{{ t('请先添加 Motif') }}</p>
  </div>
</template>
